import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { MainLayout } from '../../components/MainLayout';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Skeleton } from '../../components/ui/skeleton';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter,
} from '../../components/ui/sheet';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '../../components/ui/tooltip';
import {
  Download, RotateCcw, Search, FileText, HelpCircle, ArrowLeftRight,
  Banknote, Building2, Users, AlertCircle, CheckCircle2, X, StickyNote, Pencil,
} from 'lucide-react';

const fmtC = (n) =>
  n == null ? '$0.00' : `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  if (isNaN(d)) return v;
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: '2-digit' });
};

const TXN_TYPES = ['ACH', 'EFT', 'Credit Card', 'Check', 'Wire', 'Other'];

const StatCard = ({ label, total, anchor, customer, anchorCount, customerCount, total_count, Icon, tone, sourceSplit }) => {
  const toneCls = tone === 'pending'
    ? 'border-amber-200 bg-amber-50/40'
    : 'border-emerald-200 bg-emerald-50/40';
  const accent = tone === 'pending' ? 'text-amber-700' : 'text-emerald-700';
  return (
    <div className={`border rounded-lg p-4 ${toneCls}`} data-testid={`refund-stat-${tone}`}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className={`h-4 w-4 ${accent}`} />
        <span className={`text-xs font-semibold uppercase tracking-wide ${accent}`}>{label}</span>
      </div>
      <p className={`text-2xl font-bold ${accent}`}>{fmtC(total)}</p>
      <p className="text-[10px] text-muted-foreground mt-1">{total_count} entries</p>
      <div className="mt-2 grid grid-cols-2 gap-2 text-[11px]">
        <div className="rounded bg-white border px-2 py-1">
          <div className="text-muted-foreground">Anchor</div>
          <div className="font-semibold text-slate-800">{fmtC(anchor)}</div>
          <div className="text-[10px] text-muted-foreground">{anchorCount} entries</div>
        </div>
        <div className="rounded bg-white border px-2 py-1">
          <div className="text-muted-foreground">Customer</div>
          <div className="font-semibold text-slate-800">{fmtC(customer)}</div>
          <div className="text-[10px] text-muted-foreground">{customerCount} entries</div>
        </div>
      </div>
      {/* Source split (Refunded card only): per-bill completions vs bulk events */}
      {sourceSplit && (sourceSplit.bill_total > 0 || sourceSplit.bulk_total > 0) && (
        <div className="mt-2 rounded bg-white/60 border border-emerald-100 px-2 py-1.5 text-[11px] space-y-0.5" data-testid="refund-source-split-card">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Source</div>
          <div className="flex justify-between">
            <span className="text-slate-600">↳ Per-bill completions</span>
            <span className="font-mono">{fmtC(sourceSplit.bill_total)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-600">↳ Bulk (Process Refund)</span>
            <span className="font-mono">{fmtC(sourceSplit.bulk_total)}</span>
          </div>
        </div>
      )}
    </div>
  );
};

const HelpDrawer = ({ open, onClose }) => (
  <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
    <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto" data-testid="refund-help-drawer">
      <SheetHeader>
        <SheetTitle>How refunds flow</SheetTitle>
        <SheetDescription>What "Refund To" vs "Refunded" mean, and where each one shows up.</SheetDescription>
      </SheetHeader>
      <div className="mt-4 space-y-4 text-sm">
        <section>
          <h4 className="font-semibold mb-1">Refund TO Amount</h4>
          <p className="text-muted-foreground">
            The original amount paid by <strong>Anchor</strong> or the <strong>Customer</strong> (NOT BillPortal)
            that needs to come back to them. While the vendor still owes us back, the row is in the
            <span className="font-semibold text-amber-700"> "To Refund" </span> bucket.
          </p>
        </section>
        <section>
          <h4 className="font-semibold mb-1">Refunded</h4>
          <p className="text-muted-foreground">
            Once we receive the money back from the vendor and send it on to Anchor / Customer, the entry
            moves into <span className="font-semibold text-emerald-700">"Refunded"</span>.
          </p>
        </section>
        <section>
          <h4 className="font-semibold mb-1">Where it shows up</h4>
          <ul className="list-disc pl-5 space-y-1 text-muted-foreground">
            <li>This page — both stats + table reflect the bucket and recipient.</li>
            <li>Credit/Debit Report — a debit line of "Refund TO {`<Anchor/Customer>`}" with the same transaction reference.</li>
            <li>Org → Payments → Refunds accordion — totals split by recipient bucket.</li>
          </ul>
        </section>
        <section>
          <h4 className="font-semibold mb-1">Process Refund button</h4>
          <p className="text-muted-foreground">
            Use it whenever you actually move money back to a payer (ACH, EFT, Credit Card, …). It records the event,
            updates the totals on this page, and writes the matching debit to the Credit/Debit ledger.
          </p>
        </section>
        <div className="rounded bg-slate-50 border border-slate-200 p-3 text-[11px] text-muted-foreground">
          <strong>Tip:</strong> Refunds <em>do not</em> include amounts paid by BillPortal. Those are handled by
          variance refunds and live in the Variance Report instead.
        </div>
      </div>
    </SheetContent>
  </Sheet>
);

const ProcessRefundDrawer = ({ open, onClose, accountId, orgName, partnerName, onSuccess, editEvent, canEdit = true }) => {
  const partnerLabel = partnerName || 'Anchor';
  const isEdit = !!editEvent;
  const [form, setForm] = useState({
    refund_amount: '',
    refund_to_bucket: 'anchor',
    refund_to_name: partnerLabel,
    transaction_type: 'ACH',
    transaction_reference: '',
    refund_date: '',
    notes: '',
  });
  const [busy, setBusy] = useState(false);
  const [attachment, setAttachment] = useState(null);

  useEffect(() => {
    if (!open) return;
    if (editEvent) {
      setForm({
        refund_amount: String(editEvent.refund_amount ?? ''),
        refund_to_bucket: editEvent.refund_to_bucket || 'anchor',
        refund_to_name: editEvent.refund_to || partnerLabel,
        transaction_type: editEvent.transaction_type || 'ACH',
        transaction_reference: editEvent.transaction_reference || '',
        refund_date: editEvent.refund_date || new Date().toISOString().slice(0, 10),
        notes: editEvent.notes || '',
      });
    } else {
      setForm({
        refund_amount: '',
        refund_to_bucket: 'anchor',
        refund_to_name: partnerLabel,
        transaction_type: 'ACH',
        transaction_reference: '',
        refund_date: new Date().toISOString().slice(0, 10),
        notes: '',
      });
    }
    setAttachment(null);
  }, [open, editEvent, partnerLabel]);

  const onBucketChange = (val) => {
    let name = partnerLabel;
    if (val === 'customer') name = orgName || 'Customer';
    if (val === 'other') name = '';
    setForm((f) => ({ ...f, refund_to_bucket: val, refund_to_name: name }));
  };

  const submit = async () => {
    if (!form.refund_amount || Number(form.refund_amount) <= 0) {
      toast.error('Refund amount must be greater than zero');
      return;
    }
    if (!form.transaction_reference?.trim()) {
      toast.error('Transaction reference is required');
      return;
    }
    setBusy(true);
    try {
      const payload = {
        refund_amount: Number(form.refund_amount),
        refund_to_bucket: form.refund_to_bucket,
        refund_to_name: form.refund_to_name?.trim() || null,
        transaction_type: form.transaction_type,
        transaction_reference: form.transaction_reference.trim(),
        refund_date: form.refund_date || null,
        notes: form.notes || '',
      };
      let data;
      if (isEdit) {
        ({ data } = await api.patch(
          `/payments/v2/reports/refunds/events/${editEvent.id}?account_id=${accountId}`,
          payload,
        ));
        toast.success(`Refund updated — ${fmtC(data.event.refund_amount)} to ${data.event.refund_to}`);
      } else {
        ({ data } = await api.post(
          `/payments/v2/reports/refunds/process?account_id=${accountId}`,
          { ...payload, account_id: accountId, attachment_filename: attachment?.name || null },
        ));
        toast.success(`Refund recorded — ${fmtC(data.event.refund_amount)} to ${data.event.refund_to}`);
      }
      onSuccess?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || (isEdit ? 'Could not update refund' : 'Could not record refund'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto" data-testid="process-refund-drawer">
        <SheetHeader>
          <SheetTitle>{isEdit ? 'Edit refund' : 'Process refund'}</SheetTitle>
          <SheetDescription>
            {isEdit
              ? 'Update this refund event. Changes are mirrored to the Credit/Debit report and audit log.'
              : `Record a refund being sent back to ${partnerLabel} or the Customer. This entry is added to "Refunded" stats and also appears as a debit in the Credit/Debit report.`}
          </SheetDescription>
        </SheetHeader>
        <div className="mt-5 space-y-4">
          <div>
            <Label className="text-xs">Transaction reference *</Label>
            <Input
              value={form.transaction_reference}
              onChange={(e) => setForm({ ...form, transaction_reference: e.target.value })}
              placeholder="e.g. ACH-2026-04-991"
              data-testid="pr-txn-ref"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Refund amount *</Label>
              <Input
                type="number"
                step="0.01"
                value={form.refund_amount}
                onChange={(e) => setForm({ ...form, refund_amount: e.target.value })}
                placeholder="0.00"
                data-testid="pr-amount"
              />
            </div>
            <div>
              <Label className="text-xs">Refund date</Label>
              <Input
                type="date"
                value={form.refund_date}
                onChange={(e) => setForm({ ...form, refund_date: e.target.value })}
                data-testid="pr-date"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Refund to *</Label>
              <Select value={form.refund_to_bucket} onValueChange={onBucketChange}>
                <SelectTrigger data-testid="pr-bucket"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="anchor">{partnerLabel} (Partner)</SelectItem>
                  <SelectItem value="customer">{orgName || 'Customer'}</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Transaction type *</Label>
              <Select value={form.transaction_type} onValueChange={(v) => setForm({ ...form, transaction_type: v })}>
                <SelectTrigger data-testid="pr-txn-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TXN_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          {form.refund_to_bucket === 'other' && (
            <div>
              <Label className="text-xs">Recipient name</Label>
              <Input
                value={form.refund_to_name}
                onChange={(e) => setForm({ ...form, refund_to_name: e.target.value })}
                placeholder="e.g. vendor name"
                data-testid="pr-other-name"
              />
            </div>
          )}
          <div>
            <Label className="text-xs">Notes</Label>
            <Textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Anything internal teams should know about this refund."
              rows={3}
              data-testid="pr-notes"
            />
          </div>
          <div>
            <Label className="text-xs">Attachment (optional)</Label>
            <Input
              type="file"
              onChange={(e) => setAttachment(e.target.files?.[0] || null)}
              data-testid="pr-attachment"
            />
            {attachment && (
              <p className="text-[11px] text-muted-foreground mt-1">
                Selected: {attachment.name} ({Math.round(attachment.size / 1024)} KB) — filename stored as reference;
                full upload coming soon.
              </p>
            )}
          </div>
        </div>
        <SheetFooter className="mt-6 gap-2">
          <Button variant="ghost" size="sm" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button size="sm" onClick={submit} disabled={busy} data-testid="pr-submit">
            <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />
            {busy ? (isEdit ? 'Saving…' : 'Processing…') : (isEdit ? 'Save changes' : 'Record refund')}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

const BillDrawer = ({ open, onClose, billId, accountId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !billId) return;
    setLoading(true);
    api
      .get(`/payments/v2/reports/refunds/bill/${billId}`, { params: { account_id: accountId } })
      .then((r) => setData(r.data))
      .catch(() => toast.error('Could not load bill'))
      .finally(() => setLoading(false));
  }, [open, billId, accountId]);

  const inv = data?.invoice || {};
  const bill = data?.bill || {};
  const txns = data?.transactions || [];
  const pdfUrl = inv.billportal_url || inv.pdf_url || inv.file_url;

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="left" className="w-full sm:max-w-2xl overflow-y-auto" data-testid="refund-bill-drawer">
        <SheetHeader>
          <SheetTitle>Bill & transactions</SheetTitle>
          <SheetDescription>{bill?.transaction_reference ? `Ref: ${bill.transaction_reference}` : 'Refund preview'}</SheetDescription>
        </SheetHeader>
        {loading ? (
          <div className="mt-4 space-y-3">
            <Skeleton className="h-20" />
            <Skeleton className="h-32" />
          </div>
        ) : (
          <div className="mt-4 space-y-4 text-sm">
            <section className="rounded border p-3 bg-slate-50">
              <div className="text-xs text-muted-foreground mb-1">Invoice</div>
              <div className="font-semibold">{inv.supplier_name || bill._dn_supplier_name || '—'}</div>
              <div className="text-xs text-muted-foreground">Invoice #: {inv.invoice_number || '—'}</div>
              <div className="text-xs text-muted-foreground">Invoice date: {fmtDate(inv.invoice_date)}</div>
              <div className="text-xs text-muted-foreground">Due date: {fmtDate(inv.due_date || bill._dn_due_date)}</div>
              <div className="text-xs text-muted-foreground">Total due: {fmtC(inv.total_amount_due)}</div>
              {inv.late_payment_fee > 0 && (
                <div className="text-xs text-rose-600">Late fee: {fmtC(inv.late_payment_fee)}</div>
              )}
              {pdfUrl && (
                <a href={pdfUrl} target="_blank" rel="noopener noreferrer"
                   className="inline-flex items-center gap-1 mt-2 text-xs text-blue-600 hover:underline"
                   data-testid="refund-bill-pdf-link">
                  <FileText className="h-3 w-3" /> Open bill PDF
                </a>
              )}
            </section>

            <section>
              <div className="text-xs text-muted-foreground mb-2">Transactions ({txns.length})</div>
              {txns.length === 0 ? (
                <div className="text-xs text-muted-foreground italic">No transactions recorded on this bill.</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="h-8 text-[10px]">Date</TableHead>
                      <TableHead className="h-8 text-[10px]">Paid By</TableHead>
                      <TableHead className="h-8 text-[10px]">Method</TableHead>
                      <TableHead className="h-8 text-[10px]">Ref</TableHead>
                      <TableHead className="h-8 text-[10px] text-right">Amount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {txns.map((t) => (
                      <TableRow key={t.id}>
                        <TableCell className="text-xs">{fmtDate(t.transaction_date)}</TableCell>
                        <TableCell className="text-xs">{t.paid_by_name || '—'}</TableCell>
                        <TableCell className="text-xs">{t.payment_method || t.transaction_type || '—'}</TableCell>
                        <TableCell className="text-xs">{t.transaction_reference || '—'}</TableCell>
                        <TableCell className="text-xs text-right">{fmtC(t.amount)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </section>

            {bill.refund === 'yes' && (
              <section className="rounded border border-amber-200 bg-amber-50/40 p-3">
                <div className="text-xs font-semibold text-amber-800 mb-1">Refund detail</div>
                <div className="text-xs">To: <strong>{bill.refund_to || '—'}</strong></div>
                <div className="text-xs">Amount: {fmtC(bill.refund_amount)}</div>
                <div className="text-xs">Method: {bill.refund_method || '—'}</div>
                <div className="text-xs">Status: {bill.refund_completed ? 'Refunded' : 'To Refund'}</div>
                <div className="text-xs">Date: {fmtDate(bill.refund_date)}</div>
              </section>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default function RefundsReport() {
  const [currentAccount, setCurrentAccount] = useState(() => {
    const s = localStorage.getItem('current_account');
    return s ? JSON.parse(s) : null;
  });
  useEffect(() => {
    const check = () => {
      const s = localStorage.getItem('current_account');
      const acc = s ? JSON.parse(s) : null;
      if (JSON.stringify(acc) !== JSON.stringify(currentAccount)) setCurrentAccount(acc);
    };
    const h = setInterval(check, 800);
    return () => clearInterval(h);
  }, [currentAccount]);
  const accountId = currentAccount?.id;

  const [status, setStatus] = useState(() => {
    try {
      const sp = new URLSearchParams(window.location.search);
      const s = sp.get('status');
      if (s === 'to_refund' || s === 'refunded' || s === 'all') return s;
    } catch { /* ignore */ }
    return 'all';
  });
  const [vendor, setVendor] = useState('all');
  const [acctNumber, setAcctNumber] = useState('');
  const [bucket, setBucket] = useState('all');
  const [txnType, setTxnType] = useState('all');
  const [page, setPage] = useState(1);

  const [data, setData] = useState(null);
  const [filterOpts, setFilterOpts] = useState({ vendors: [], account_numbers: [], transaction_types: [] });
  const [loading, setLoading] = useState(true);
  const [helpOpen, setHelpOpen] = useState(false);
  const [processOpen, setProcessOpen] = useState(false);
  const [editEvent, setEditEvent] = useState(null);
  const [billDrawer, setBillDrawer] = useState({ open: false, billId: null });

  // Check write capability — same gate as backend uses for PATCH events
  const canEditRefunds = (() => {
    try {
      const u = JSON.parse(localStorage.getItem('auth') || '{}')?.user || {};
      if (u.is_super_admin) return true;
      const role = (u.role || '').toLowerCase();
      if (['super_admin', 'admin', 'owner', 'finance_editor', 'payments_admin'].includes(role)) return true;
      const caps = u.capabilities || [];
      return caps.includes('payments.edit') || caps.includes('refunds.edit');
    } catch { return false; }
  })();

  const load = useCallback(async () => {
    if (!accountId) return;
    setLoading(true);
    try {
      const params = { account_id: accountId, status, page, limit: 50 };
      if (vendor && vendor !== 'all') params.vendor = vendor;
      if (acctNumber.trim()) params.account_number = acctNumber.trim();
      if (bucket && bucket !== 'all') params.refund_to = bucket;
      if (txnType && txnType !== 'all') params.transaction_type = txnType;
      const { data: d } = await api.get('/payments/v2/reports/refunds', { params });
      setData(d);
    } catch {
      toast.error('Could not load refunds report');
    } finally {
      setLoading(false);
    }
  }, [accountId, status, vendor, acctNumber, bucket, txnType, page]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!accountId) return;
    api
      .get('/payments/v2/reports/refunds/filter-options', { params: { account_id: accountId } })
      .then((r) => setFilterOpts(r.data))
      .catch(() => {});
  }, [accountId]);

  const rows = data?.rows || [];
  const events = data?.events || [];
  const summary = data?.summary || { to_refund: {}, refunded: {} };
  const total = data?.total || 0;
  const totalPages = data?.total_pages || 0;
  const orgName = data?.org_name || currentAccount?.organisation_name || '';
  // Partner display name: prefer backend-resolved value (data.partner_name),
  // fall back to denormalised account fields, else null (no-partner orgs).
  const partnerName = data?.partner_name
    || currentAccount?.partner_name
    || currentAccount?.bp_partner_name
    || null;

  const downloadCsv = () => {
    if (!accountId) return;
    const qs = new URLSearchParams({ account_id: accountId, status });
    if (vendor && vendor !== 'all') qs.set('vendor', vendor);
    if (acctNumber.trim()) qs.set('account_number', acctNumber.trim());
    if (bucket && bucket !== 'all') qs.set('refund_to', bucket);
    if (txnType && txnType !== 'all') qs.set('transaction_type', txnType);
    const token = JSON.parse(localStorage.getItem('auth') || '{}')?.access_token;
    fetch(`${api.defaults.baseURL}/payments/v2/reports/refunds/export?${qs.toString()}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `refunds_${(orgName || 'org').replace(/\s+/g, '_')}_${new Date().toISOString().slice(0, 10)}.csv`;
        link.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => toast.error('Could not download'));
  };

  const StatusBadge = ({ completed }) => completed ? (
    <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100" data-testid="refund-status-badge-done">Refunded</Badge>
  ) : (
    <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100" data-testid="refund-status-badge-pending">To Refund</Badge>
  );

  return (
    <MainLayout wide>
      <TooltipProvider delayDuration={200}>
        <div className="space-y-4 p-4 md:p-6" data-testid="refunds-report-page">
          {/* Header */}
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-amber-500 to-rose-500 flex items-center justify-center shadow-sm">
                <ArrowLeftRight className="h-4 w-4 text-white" strokeWidth={2.5} />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-xl font-bold" data-testid="refunds-title">Refunds Report</h1>
                  <button
                    onClick={() => setHelpOpen(true)}
                    className="inline-flex items-center gap-1 text-[11px] text-blue-600 hover:underline"
                    data-testid="refunds-help-btn"
                  >
                    <HelpCircle className="h-3 w-3" /> help
                  </button>
                </div>
                <p className="text-[11px] text-muted-foreground">
                  Refund-to amounts (paid by {partnerName || 'Partner'} or Customer, not BillPortal) and refunds actually processed for {orgName}.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={downloadCsv} disabled={loading} data-testid="refunds-download-csv">
                <Download className="h-3.5 w-3.5 mr-1" /> Download
              </Button>
              <Button size="sm" onClick={() => setProcessOpen(true)} data-testid="refunds-process-btn">
                <Banknote className="h-3.5 w-3.5 mr-1" /> Process Refund
              </Button>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <StatCard
              label="To Refund (Pending)"
              total={summary.to_refund?.total} anchor={summary.to_refund?.anchor} customer={summary.to_refund?.customer}
              anchorCount={summary.to_refund?.anchor_count} customerCount={summary.to_refund?.customer_count}
              total_count={summary.to_refund?.count}
              Icon={AlertCircle} tone="pending"
            />
            <StatCard
              label="Refunded (Completed)"
              total={summary.refunded?.total} anchor={summary.refunded?.anchor} customer={summary.refunded?.customer}
              anchorCount={summary.refunded?.anchor_count} customerCount={summary.refunded?.customer_count}
              total_count={summary.refunded?.count}
              Icon={CheckCircle2} tone="done"
              sourceSplit={{
                bill_total: summary.refunded_bill?.total || 0,
                bulk_total: summary.refunded_bulk?.total || 0,
              }}
            />
          </div>

          {/* Filters */}
          <div className="border rounded-lg p-3 bg-card flex flex-wrap gap-2 items-center" data-testid="refunds-filters">
            <Select value={status} onValueChange={(v) => { setStatus(v); setPage(1); }}>
              <SelectTrigger className="h-8 w-40 text-xs" data-testid="refunds-filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="to_refund">To Refund only</SelectItem>
                <SelectItem value="refunded">Refunded only</SelectItem>
              </SelectContent>
            </Select>
            <Select value={vendor} onValueChange={(v) => { setVendor(v); setPage(1); }}>
              <SelectTrigger className="h-8 w-48 text-xs" data-testid="refunds-filter-vendor"><SelectValue placeholder="Vendor" /></SelectTrigger>
              <SelectContent className="max-h-80">
                <SelectItem value="all">All vendors</SelectItem>
                {(filterOpts.vendors || []).map((v) => (
                  <SelectItem key={v.name} value={v.name}>{v.name} ({v.count})</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={bucket} onValueChange={(v) => { setBucket(v); setPage(1); }}>
              <SelectTrigger className="h-8 w-40 text-xs" data-testid="refunds-filter-bucket"><SelectValue placeholder="Refund To" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any recipient</SelectItem>
                <SelectItem value="anchor">{partnerName || 'Anchor'}</SelectItem>
                <SelectItem value="customer">{orgName || 'Customer'}</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
            <Select value={txnType} onValueChange={(v) => { setTxnType(v); setPage(1); }}>
              <SelectTrigger className="h-8 w-36 text-xs" data-testid="refunds-filter-txntype"><SelectValue placeholder="Txn Type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any txn type</SelectItem>
                {TXN_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
              <Input
                value={acctNumber} onChange={(e) => { setAcctNumber(e.target.value); setPage(1); }}
                placeholder="Account #" className="h-8 w-40 text-xs pl-6"
                data-testid="refunds-filter-acctnum"
              />
            </div>
            <Button variant="ghost" size="sm" onClick={() => {
              setStatus('all'); setVendor('all'); setAcctNumber(''); setBucket('all'); setTxnType('all'); setPage(1);
            }} className="h-8 text-xs" data-testid="refunds-filter-reset">
              <RotateCcw className="h-3 w-3 mr-1" /> Reset
            </Button>
            <div className="ml-auto text-[11px] text-muted-foreground">
              {loading ? 'Loading…' : `${total.toLocaleString()} rows`}
            </div>
          </div>

          {/* Table */}
          <div className="rounded-lg border bg-card overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/40">
                  <TableHead className="text-[10px] uppercase">Property / Address</TableHead>
                  <TableHead className="text-[10px] uppercase">Account #</TableHead>
                  <TableHead className="text-[10px] uppercase">Vendor</TableHead>
                  <TableHead className="text-[10px] uppercase">PF Batch</TableHead>
                  <TableHead className="text-[10px] uppercase">Refund Date</TableHead>
                  <TableHead className="text-[10px] uppercase">Paid By</TableHead>
                  <TableHead className="text-[10px] uppercase">Refund To</TableHead>
                  <TableHead className="text-[10px] uppercase text-right">Paid Amt (Refund To)</TableHead>
                  <TableHead className="text-[10px] uppercase text-right">Refunded</TableHead>
                  <TableHead className="text-[10px] uppercase">Status</TableHead>
                  <TableHead className="text-[10px] uppercase w-8"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && rows.length === 0 && (
                  Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={`s${i}`}>
                      <TableCell colSpan={11}><Skeleton className="h-6 w-full" /></TableCell>
                    </TableRow>
                  ))
                )}
                {!loading && rows.length === 0 && events.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={11} className="text-center text-xs text-muted-foreground py-8">
                      No refunds to display.
                    </TableCell>
                  </TableRow>
                )}
                {/* Standalone refund events (manual entries via Process Refund) —
                    rendered as gray rows above the bill grid. They share the table
                    structure but only carry date / ref / amount / notes. */}
                {events.map((ev) => (
                  <TableRow
                    key={`ev_${ev.event_id}`}
                    className="bg-slate-50/70 hover:bg-slate-100"
                    data-testid={`refund-event-row-${ev.event_id}`}
                  >
                    <TableCell className="text-xs">
                      <div className="font-medium text-slate-600 italic">Manual refund entry</div>
                      <div className="text-[10px] text-muted-foreground">via Process Refund</div>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">—</TableCell>
                    <TableCell className="text-xs text-muted-foreground">—</TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      <span className="font-mono text-[10px]" title={`Ref: ${ev.transaction_reference || ''}`}>
                        {ev.transaction_reference || '—'}
                      </span>
                      {ev.transaction_type && (
                        <div className="text-[10px] text-muted-foreground">{ev.transaction_type}</div>
                      )}
                    </TableCell>
                    <TableCell className="text-xs">{fmtDate(ev.refund_date)}</TableCell>
                    <TableCell className="text-xs">{ev.paid_by_name || '—'}</TableCell>
                    <TableCell className="text-xs">
                      <div className="flex items-center gap-1">
                        {ev.refund_to_bucket === 'anchor' && <Building2 className="h-3 w-3 text-blue-500" />}
                        {ev.refund_to_bucket === 'customer' && <Users className="h-3 w-3 text-emerald-500" />}
                        <span>{ev.refund_to || '—'}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-xs text-right text-muted-foreground">—</TableCell>
                    <TableCell className="text-xs text-right font-medium">{fmtC(ev.refunded_amount)}</TableCell>
                    <TableCell>
                      <Badge className="bg-slate-200 text-slate-700 hover:bg-slate-200">Refunded</Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-0.5">
                        {ev.notes ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                type="button"
                                className="rounded p-1 hover:bg-slate-200 text-slate-500"
                                data-testid={`refund-event-note-${ev.event_id}`}
                              >
                                <StickyNote className="h-3.5 w-3.5" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent side="left" className="max-w-[280px] text-xs whitespace-pre-wrap">
                              <div className="font-semibold mb-1">Note</div>
                              {ev.notes}
                              {ev.attachment_filename && (
                                <div className="text-[10px] mt-1 text-muted-foreground italic">
                                  Attachment: {ev.attachment_filename}
                                </div>
                              )}
                            </TooltipContent>
                          </Tooltip>
                        ) : (
                          <span className="text-muted-foreground inline-block w-4">—</span>
                        )}
                        {canEditRefunds && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                type="button"
                                onClick={() => {
                                  setEditEvent({
                                    id: ev.event_id,
                                    refund_amount: ev.refunded_amount,
                                    refund_to_bucket: ev.refund_to_bucket,
                                    refund_to: ev.refund_to,
                                    transaction_type: ev.transaction_type,
                                    transaction_reference: ev.transaction_reference,
                                    refund_date: ev.refund_date,
                                    notes: ev.notes,
                                  });
                                  setProcessOpen(true);
                                }}
                                className="rounded p-1 hover:bg-emerald-100 text-emerald-700"
                                data-testid={`refund-event-edit-${ev.event_id}`}
                              >
                                <Pencil className="h-3.5 w-3.5" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent>Edit refund</TooltipContent>
                          </Tooltip>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {rows.map((r) => (
                  <TableRow key={`${r.bill_id}_${r.payment_file_id}`} data-testid={`refund-row-${r.bill_id}`}>
                    <TableCell className="text-xs">
                      <div className="font-medium">{r.property_name || '—'}</div>
                      <div className="text-[10px] text-muted-foreground">{r.service_address || r.property_address || '—'}</div>
                    </TableCell>
                    <TableCell className="text-xs">{r.account_number || '—'}</TableCell>
                    <TableCell className="text-xs">{r.supplier_name || '—'}</TableCell>
                    <TableCell className="text-xs">{r.pf_batch_name || '—'}
                      {r.pf_status && <div className="text-[10px] text-muted-foreground">{r.pf_status}</div>}
                    </TableCell>
                    <TableCell className="text-xs">{fmtDate(r.refund_date || r.transaction_date)}</TableCell>
                    <TableCell className="text-xs">{r.paid_by_name || '—'}</TableCell>
                    <TableCell className="text-xs">
                      <div className="flex items-center gap-1">
                        {r.refund_to_bucket === 'anchor' && <Building2 className="h-3 w-3 text-blue-500" />}
                        {r.refund_to_bucket === 'customer' && <Users className="h-3 w-3 text-emerald-500" />}
                        <span>{r.refund_to || '—'}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-xs text-right font-medium">{fmtC(r.paid_amount)}</TableCell>
                    <TableCell className="text-xs text-right font-medium">
                      {r.refunded_amount != null ? fmtC(r.refunded_amount) : '—'}
                    </TableCell>
                    <TableCell><StatusBadge completed={r.refund_completed} /></TableCell>
                    <TableCell>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            onClick={() => setBillDrawer({ open: true, billId: r.bill_id })}
                            className="rounded p-1 hover:bg-slate-100 text-slate-500"
                            data-testid={`refund-view-bill-${r.bill_id}`}
                          >
                            <FileText className="h-3.5 w-3.5" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>View bill & transactions</TooltipContent>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-between items-center text-xs">
              <span className="text-muted-foreground">Page {page} of {totalPages}</span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</Button>
                <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</Button>
              </div>
            </div>
          )}
        </div>
      </TooltipProvider>

      <HelpDrawer open={helpOpen} onClose={() => setHelpOpen(false)} />
      <ProcessRefundDrawer
        open={processOpen}
        onClose={() => { setProcessOpen(false); setEditEvent(null); }}
        accountId={accountId}
        orgName={orgName}
        partnerName={partnerName}
        onSuccess={() => load()}
        editEvent={editEvent}
        canEdit={canEditRefunds}
      />
      <BillDrawer
        open={billDrawer.open}
        onClose={() => setBillDrawer({ open: false, billId: null })}
        billId={billDrawer.billId}
        accountId={accountId}
      />
    </MainLayout>
  );
}
